import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import * as React from 'react';

interface UnsavedChangesModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void | Promise<void>;
  onDiscard: () => void | Promise<void>;
  loading?: boolean;
  title?: string;
  subtitle?: string;
  callout?: string;
  message?: string;
  saveLabel?: string;
  discardLabel?: string;
  cancelLabel?: string;
}

const UnsavedChangesModal: React.FC<UnsavedChangesModalProps> = ({
  open,
  onClose,
  onSave,
  onDiscard,
  loading = false,
  title = strings.unsavedChangesTitle,
  subtitle = strings.unsavedChangesSubtitle,
  callout = strings.unsavedChangesCallout,
  message = strings.unsavedChangesBody,
  saveLabel = strings.unsavedChangesSaveContinue,
  discardLabel = strings.unsavedChangesDiscard,
  cancelLabel = strings.cancelText,
}) => (
  <Dialog
    open={open}
    maxWidth="sm"
    fullWidth
    onClose={loading ? undefined : onClose}
    aria-labelledby="unsaved-changes-modal-title"
    aria-describedby="unsaved-changes-modal-description"
    PaperProps={{
      sx: {
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.14)',
      },
    }}>
    <Box
      sx={{
        px: 3,
        pt: 3,
        pb: 2,
        background: 'linear-gradient(180deg, rgba(237, 108, 2, 0.08) 0%, transparent 100%)',
      }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            bgcolor: 'rgba(237, 108, 2, 0.12)',
            color: '#ED6C02',
          }}>
          <EditNoteOutlinedIcon sx={{ fontSize: 28 }} />
        </Box>
        <Box sx={{ minWidth: 0, pt: 0.5 }}>
          <Typography id="unsaved-changes-modal-title" variant="h6" fontWeight={700} lineHeight={1.3}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Box>

    <DialogContent id="unsaved-changes-modal-description" sx={{ px: 3, pt: 1, pb: 2 }}>
      <Box
        sx={{
          mb: 2,
          px: 2,
          py: 1.5,
          borderRadius: 2,
          bgcolor: 'rgba(237, 108, 2, 0.08)',
          border: '1px solid',
          borderColor: 'rgba(237, 108, 2, 0.35)',
        }}>
        <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ lineHeight: 1.5 }}>
          {callout}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {message}
      </Typography>
    </DialogContent>

    <DialogActions
      sx={{
        px: 3,
        py: 2,
        gap: 1,
        flexWrap: 'wrap',
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'grey.50',
      }}>
      <Button
        variant="outlined"
        color="inherit"
        onClick={onClose}
        disabled={loading}
        sx={{
          minWidth: 96,
          textTransform: 'none',
          fontWeight: 600,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}>
        {cancelLabel}
      </Button>
      <Box sx={{ flex: 1 }} />
      <Button
        variant="outlined"
        color="error"
        onClick={onDiscard}
        disabled={loading}
        sx={{
          minWidth: 132,
          textTransform: 'none',
          fontWeight: 600,
        }}>
        {discardLabel}
      </Button>
      <Button
        variant="contained"
        onClick={onSave}
        autoFocus
        disabled={loading}
        startIcon={<SaveOutlinedIcon />}
        sx={{
          minWidth: 148,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        }}>
        {loading ? strings.unsavedChangesSaving : saveLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

export default UnsavedChangesModal;
